import { useEffect, useState } from "react";
import { X, Loader2, GitCompare, ArrowRight, TrendingUp, TrendingDown, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { clp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Modal de comparación lado-a-lado de 2 vehículos del historial de patentes.
 * Recibe `items=[chipA, chipB]` con {patente, marca, modelo, anio, avaluo, tasacion}.
 * Calcula el total de transferencia llamando /api/simulator/quote para cada uno
 * y muestra la diferencia destacada.
 */
export const PlatesComparator = ({ items, onClose, region = "Metropolitana" }) => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.all(items.map((it) =>
          api.post("/api/simulator/quote", {
            patente: it.patente,
            valor: it.tasacion || it.avaluo || 0,
            avaluo_fiscal: it.avaluo || null,
            region, requiere_notaria: true, requiere_informe: true, requiere_express: false,
          }).then((r) => r.data).catch(() => null)
        ));
        if (active) {
          const map = {};
          items.forEach((it, idx) => { map[it.patente] = results[idx]; });
          setQuotes(map);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [items, region]);

  const iniciar = (it) => {
    const prefill = {
      patente: it.patente, valor: it.tasacion || it.avaluo || 0,
      avaluo_fiscal: it.avaluo, region,
      requiere_notaria: true, requiere_informe: true, requiere_express: false,
    };
    if (user) navigate("/nueva-transferencia", { state: { prefill } });
    else login();
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const resp = await api.post(
        "/api/comparador/pdf",
        { items: items.map((it) => ({
            patente: it.patente, marca: it.marca || "", modelo: it.modelo || "",
            anio: it.anio || null, avaluo: it.avaluo || 0, tasacion: it.tasacion || 0,
          })), region },
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `comparativo-${items[0].patente}-vs-${items[1].patente}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF comparativo descargado");
    } catch {
      toast.error("No pudimos generar el PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (items.length !== 2) return null;
  const [a, b] = items;
  const qa = quotes[a.patente];
  const qb = quotes[b.patente];
  const diff = qa && qb ? (qa.total - qb.total) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-testid="comparator-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-[#F5B301] font-bold inline-flex items-center gap-1">
              <GitCompare size={11} /> Comparador
            </p>
            <h3 className="font-display font-bold text-xl text-[#13315C]">Lado a lado</h3>
          </div>
          <div className="flex items-center gap-2">
            {!loading && (
              <button
                onClick={downloadPdf}
                disabled={downloadingPdf}
                data-testid="comparator-pdf-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#13315C] hover:text-[#F5B301] disabled:opacity-50 transition-colors px-2 py-1 rounded"
                title="Descargar comparativo en PDF"
              >
                {downloadingPdf ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />}
                PDF
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-[#13315C]" data-testid="comparator-close">
              <X size={20} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-16 flex flex-col items-center justify-center text-slate-500">
            <Loader2 className="animate-spin text-[#13315C]" size={28} />
            <p className="mt-3 text-sm">Calculando totales para los dos vehículos…</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 divide-x divide-slate-200" data-testid="comparator-grid">
            {[{ it: a, q: qa, side: "A" }, { it: b, q: qb, side: "B" }].map(({ it, q, side }) => {
              const isWinner = diff !== null && ((side === "A" && qa.total < qb.total) || (side === "B" && qb.total < qa.total));
              return (
                <div key={it.patente} className={`p-5 ${isWinner ? "bg-emerald-50/40" : ""}`} data-testid={`comparator-side-${side.toLowerCase()}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono-tf font-black text-lg text-[#13315C] tracking-wider">{it.patente}</span>
                    {isWinner && (
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 uppercase tracking-wide" data-testid={`comparator-winner-${side.toLowerCase()}`}>
                        Más barato
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-slate-700 leading-tight">
                    {it.marca || "—"} {it.modelo || ""}
                  </p>
                  <p className="text-xs text-slate-500">Año {it.anio || "—"}</p>

                  <dl className="mt-4 space-y-2 text-xs">
                    <Row label="Avalúo SII" value={it.avaluo ? clp(it.avaluo) : "—"} />
                    <Row label="Tasación mercado" value={it.tasacion ? clp(it.tasacion) : "—"} />
                    <Row label="Impuesto (1,5%)" value={q ? clp(q.items.find((x) => x.key === "impuesto")?.amount) : "—"} />
                    <Row label="Aranceles RC" value={q ? clp(q.items.find((x) => x.key === "aranceles")?.amount) : "—"} />
                    <Row label="Servicios + firmas" value={q ? clp((q.items.find((x) => x.key === "servicios")?.amount || 0) + (q.items.find((x) => x.key === "firma")?.amount || 0) + (q.items.find((x) => x.key === "biometria")?.amount || 0)) : "—"} />
                    <Row label="Informe 360 + notaría" value={q ? clp((q.items.find((x) => x.key === "informe")?.amount || 0) + (q.items.find((x) => x.key === "notarial")?.amount || 0)) : "—"} />
                  </dl>

                  <div className="mt-4 pt-3 border-t border-slate-200">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Total transferencia</p>
                    <p className={`font-display font-black text-2xl ${isWinner ? "text-emerald-700" : "text-[#13315C]"}`} data-testid={`comparator-total-${side.toLowerCase()}`}>
                      {q ? clp(q.total) : "—"}
                    </p>
                  </div>

                  <Button onClick={() => iniciar(it)} className={`w-full mt-4 rounded-md h-9 text-xs font-semibold ${isWinner ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[#13315C] hover:bg-[#1C4076] text-white"}`} data-testid={`comparator-cta-${side.toLowerCase()}`}>
                    Iniciar con {it.patente} <ArrowRight size={13} className="ml-1" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {!loading && diff !== null && (
          <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-center gap-2 text-sm" data-testid="comparator-diff">
            {diff > 0 ? (
              <><TrendingUp size={15} className="text-amber-600" /> <span className="text-slate-700">El <b>A</b> es <b className="text-amber-700">{clp(Math.abs(diff))}</b> más caro que el <b>B</b></span></>
            ) : diff < 0 ? (
              <><TrendingDown size={15} className="text-emerald-600" /> <span className="text-slate-700">El <b>A</b> es <b className="text-emerald-700">{clp(Math.abs(diff))}</b> más barato que el <b>B</b></span></>
            ) : (
              <span className="text-slate-500">Mismo costo total para ambos vehículos.</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-2">
    <dt className="text-slate-500">{label}</dt>
    <dd className="font-mono-tf font-semibold text-[#13315C] whitespace-nowrap">{value}</dd>
  </div>
);
